import { CrewFileUploader } from 'components/devextreme/crewFileUploader/crewFileUploader'
import { UploadFile } from 'models/domain/uploadFile'
import { CrewErrorBoundary } from 'components/functions/crewErrorBoundary'
import { FileUploadConfirmDialog } from 'features/file/components/fileUploadConfirmDialog/fileUploadConfirmDialog'
import { memo, useCallback, useState } from 'react'
import { GroupChatFileListToolbar } from './components/groupChatFileListToolbar/groupChatFileListToolbar'
import { useProjectPermissions } from '@crew/hooks'
import { useParams } from 'react-router-dom'
import { EntityType } from '@crew/enums/domain'
import { useToast } from 'hooks/useToast'
import { useTranslation } from '@crew/modules/i18n'
import { useAppDispatch } from 'states/hooks'
import { useBulkInsertFilesMutation } from '@crew/apis/file/fileApis'
import { useModal } from 'components/layouts/modal/useModal'
import { useShowApiErrors } from 'hooks/useShowApiErrors'
import { FormValues } from 'features/file/components/fileUploadConfirmDialog/components/fileUploadConfirmForm/useFileUploadConfirmForm'
import {
  ObjectEventMessage,
  notifyFileEvent,
} from 'features/app/states/appSlice'
import { NotifyEventType, UnsortedFolder } from 'enums/app'
import { FileTable } from './components/fileTable/fileTable'
import { CrewFolderList } from 'components/elements/crewFolderList/crewFolderList'

export const GroupChatFileList = memo(() => {
  const toast = useToast()
  const { t } = useTranslation()
  const { projectId } = useParams()
  const dispatch = useAppDispatch()
  const [selectedFolderId, setSelectedFolderId] = useState<string>(
    UnsortedFolder.value
  )
  const [isShowSearchCondition, setIsShowSearchCondition] = useState(false)
  const { hasPrjFileCreatePermission } = useProjectPermissions(
    EntityType.Project,
    projectId
  )

  // アップロード済みのファイル一覧
  const [uploadedFileList, setUploadedFileList] = useState<UploadFile[]>([])

  const [bulkInsertFilesMutation, { isLoading: isLoadingBulkInsertFiles }] =
    useBulkInsertFilesMutation()

  const [
    isFileUploadConfirmDialogOpen,
    openFileUploadConfirmDialog,
    closeFileUploadConfirmDialog,
  ] = useModal()

  const [generalShowApiErrors] = useShowApiErrors()

  // アップロードファイル確認ダイアログのキャンセル時
  const handleCancel = useCallback(() => {
    // アップロード済ファイル情報のクリア
    setUploadedFileList([])

    // ダイアログをクローズ
    closeFileUploadConfirmDialog()
  }, [closeFileUploadConfirmDialog])

  const handleSubmit = useCallback(
    async (data: FormValues) => {
      try {
        if (!projectId) {
          return
        }

        // uploadFileList 型は、一括挿入ファイル要求の Files 型とは異なるため、ファイルのデータ型をマッピングする必要がある
        const files = uploadedFileList.map((file) => {
          return {
            name: file.name,
            keyName: file.keyName ?? '',
          }
        })
        try {
          // folderIdを指定してアップロードする
          const folderId =
            !isShowSearchCondition && selectedFolderId !== UnsortedFolder.value
              ? selectedFolderId
              : undefined

          // NOTE: アップロードしたファイル情報はuploadedFileListから取得
          await bulkInsertFilesMutation({
            entityType: EntityType.Project,
            entityRecordId: projectId,
            folderId,
            fileHistoryDescription: data.description,
            files,
            needNotification: data.needNotification,
          }).unwrap()

          const objectEventMessage: ObjectEventMessage<File> = {
            eventType: NotifyEventType.Inserted,
            id: files[0].keyName,
            object: undefined,
          }
          // ファイル一覧を更新
          dispatch(notifyFileEvent(objectEventMessage))

          toast.success(t('message.file.uploadSuccess'))

          // アップロード済ファイル情報のクリア
          setUploadedFileList([])
        } catch (err) {
          toast.error(t('message.file.uploadFailed'))
        }
        // ダイアログをクローズ
        closeFileUploadConfirmDialog()
      } catch (err) {
        generalShowApiErrors(err)
      }
    },
    [
      bulkInsertFilesMutation,
      closeFileUploadConfirmDialog,
      dispatch,
      generalShowApiErrors,
      isShowSearchCondition,
      projectId,
      selectedFolderId,
      t,
      toast,
      uploadedFileList,
    ]
  )

  // アップロード完了後
  const handleUploaded = useCallback(
    (file: UploadFile) => {
      // アップロードしたファイル情報を配列に格納
      setUploadedFileList((baseData) => {
        const index = baseData.findIndex((item) => item.name === file.name)
        if (index === -1) {
          return [...baseData, file]
        } else {
          // 同じ名前の既存ファイル情報を置き換える
          baseData[index] = file
          return [...baseData]
        }
      })

      // 確認ダイアログを表示
      openFileUploadConfirmDialog()
    },
    [openFileUploadConfirmDialog]
  )

  // フォルダーアイテムがクリックされたときのイベントハンドラ
  const handleFolderItemClick = useCallback((folderId: string) => {
    setSelectedFolderId(folderId)
  }, [])

  // 詳細検索ボタンクリック時
  const handleSearchConditionButtonClick = useCallback(() => {
    setIsShowSearchCondition((prev) => !prev)
  }, [])

  // アップロードエラー時
  const handleUploadError = useCallback(
    (file: UploadFile) => {
      // uploadedFileListに格納している該当ファイル情報を削除する
      setUploadedFileList((baseData) =>
        baseData.filter((item) => item.name !== file.name)
      )

      // アップロード済ファイル情報が0件の場合、確認ダイアログをクローズ
      if (uploadedFileList.length === 0) {
        closeFileUploadConfirmDialog()
      }
    },
    [closeFileUploadConfirmDialog, uploadedFileList.length]
  )

  return (
    <div className="flex flex-col h-full w-full overflow-auto">
      <CrewErrorBoundary>
        <GroupChatFileListToolbar
          onSearchConditionButtonClick={handleSearchConditionButtonClick}
          isShowSearchCondition={isShowSearchCondition}
        />
      </CrewErrorBoundary>
      <CrewErrorBoundary>
        <div className="gap-1 flex flex-row grow px-2.5">
          {projectId && !isShowSearchCondition && (
            <div className="w-52 shrink-0">
              <CrewFolderList
                entityType={EntityType.Project}
                entityRecordId={projectId}
                onFolderItemClick={handleFolderItemClick}
                showContextMenu={true}
                disableDragAndDrop={false}
              />
            </div>
          )}

          <div className="flex flex-col h-full grow min-w-0">
            <CrewFileUploader
              multiple={true}
              onUploaded={handleUploaded}
              onUploadError={handleUploadError}
              disabled={!hasPrjFileCreatePermission}
              isCancelUpload={!isFileUploadConfirmDialogOpen}
            >
              <div className="pt-2.5">
                <FileTable
                  folderId={selectedFolderId}
                  isShowSearchCondition={isShowSearchCondition}
                />
              </div>
            </CrewFileUploader>
          </div>
        </div>

        <FileUploadConfirmDialog
          title={t('label.fileUpload')}
          isOpen={isFileUploadConfirmDialogOpen}
          onClose={handleCancel}
          onSubmit={handleSubmit}
          uploadFiles={uploadedFileList}
          isSubmitting={isLoadingBulkInsertFiles}
        />
      </CrewErrorBoundary>
    </div>
  )
})
